/**
 * 상황별 자동 피드백 요청 시스템
 * 사용자의 행동과 상황에 따라 자동으로 피드백을 요청
 */

// 피드백 트리거 조건 설정
const FEEDBACK_TRIGGERS = {
  // 성공 상황 기반 트리거
  success: {
    // 문제를 연속으로 맞췄을 때
    correctStreak: {
      threshold: 5,
      message: "연속으로 문제를 맞히고 계시네요! 👏 지금까지의 경험은 어떠신가요?",
      delay: 2000,
      priority: 'high',
      cooldown: 300000 // 5분 쿨다운
    },
    // 시험을 완료했을 때
    examCompleted: {
      threshold: 1,
      message: "시험을 완료하셨네요! 🎉 전반적인 시험 경험에 대해 의견을 주세요!",
      delay: 3000,
      priority: 'high',
      cooldown: 600000 // 10분 쿨다운
    },
    // 높은 점수를 달성했을 때
    highScore: {
      threshold: 80,
      message: "우수한 점수를 받으셨네요! ⭐ 시스템이 도움이 되었나요?",
      delay: 1500,
      priority: 'medium',
      cooldown: 900000 // 15분 쿨다운
    }
  },

  // 어려움 상황 기반 트리거
  difficulty: {
    // 연속으로 틀렸을 때
    wrongStreak: {
      threshold: 3,
      message: "어려운 문제들이 연속으로 나왔네요 😅 개선할 점이 있다면 알려주세요!",
      delay: 1000,
      priority: 'medium',
      cooldown: 180000 // 3분 쿨다운
    },
    // 같은 유형 문제를 반복해서 틀릴 때
    repeatMistakes: {
      threshold: 2,
      message: "비슷한 유형의 문제에서 어려움을 겪고 계시네요 🤔 해설이나 문제 구성에 대한 의견이 있으시면 말씀해주세요!",
      delay: 2500,
      priority: 'high',
      cooldown: 240000 // 4분 쿨다운
    }
  },

  // 사용 패턴 기반 트리거
  usage: {
    // 긴 시간 사용했을 때
    longSession: {
      threshold: 1800000, // 30분
      message: "오랫동안 열심히 공부하고 계시네요! 📚 장시간 사용 경험은 어떠신가요?",
      delay: 0,
      priority: 'low',
      cooldown: 3600000 // 1시간 쿨다운
    },
    // 자주 방문하는 사용자
    frequentUser: {
      threshold: 7, // 7일 중 5일 이상
      message: "자주 방문해주셔서 감사합니다! 🌟 계속 사용하게 만드는 요소가 있다면 공유해주세요!",
      delay: 5000,
      priority: 'medium',
      cooldown: 2592000000 // 30일 쿨다운
    },
    // 특정 기능을 처음 사용했을 때
    featureDiscovery: {
      threshold: 1,
      message: "새로운 기능을 발견하셨네요! ✨ 이 기능에 대한 첫인상을 알려주세요!",
      delay: 3000,
      priority: 'medium',
      cooldown: 86400000 // 24시간 쿨다운
    }
  },

  // 에러/문제 상황 기반 트리거
  issues: {
    // 페이지 로딩이 오래 걸릴 때
    slowLoading: {
      threshold: 5000, // 5초
      message: "페이지 로딩이 평소보다 느린 것 같네요 ⏰ 성능 문제를 경험하고 계신가요?",
      delay: 1000,
      priority: 'high',
      cooldown: 300000 // 5분 쿨다운
    },
    // JavaScript 에러가 발생했을 때
    jsError: {
      threshold: 1,
      message: "일시적인 오류가 발생한 것 같습니다 🔧 불편을 드려 죄송합니다. 어떤 상황이었는지 알려주시겠어요?",
      delay: 2000,
      priority: 'critical',
      cooldown: 600000 // 10분 쿨다운
    }
  },

  // 이벤트 기반 트리거
  events: {
    // 페이지를 떠나려고 할 때 (beforeunload)
    pageExit: {
      threshold: 1,
      message: "떠나기 전에 잠깐! 👋 오늘의 학습 경험에 대한 간단한 의견을 남겨주세요!",
      delay: 0,
      priority: 'medium',
      cooldown: 3600000, // 1시간 쿨다운
      isExitIntent: true
    },
    // 모바일에서 앱을 백그라운드로 보낼 때
    appBackground: {
      threshold: 1,
      message: "모바일 사용 경험은 어떠셨나요? 📱 개선점이 있다면 알려주세요!",
      delay: 500,
      priority: 'low',
      cooldown: 1800000 // 30분 쿨다운
    }
  }
};

// 트리거 조건 체크 및 관리 클래스
export class AutoFeedbackTrigger {
  constructor() {
    this.sessionData = {
      startTime: Date.now(),
      correctAnswers: 0,
      wrongAnswers: 0,
      correctStreak: 0,
      wrongStreak: 0,
      completedExams: 0,
      visitDays: this.getVisitDays(),
      usedFeatures: new Set(),
      lastFeedbackTrigger: {},
      recentMistakeTypes: []
    };

    this.eventListeners = [];
    this.initializeTracking();
  }

  // 방문 일수 추적
  getVisitDays() {
    const visits = JSON.parse(localStorage.getItem('userVisits') || '[]');
    const today = new Date().toDateString();
    
    if (!visits.includes(today)) {
      visits.push(today);
      // 최근 30일만 유지
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toDateString();
      const recentVisits = visits.filter(visit => visit >= thirtyDaysAgo);
      localStorage.setItem('userVisits', JSON.stringify(recentVisits));
      return recentVisits.length;
    }
    
    return visits.length;
  }

  // 이벤트 추적 초기화
  initializeTracking() {
    // 페이지 로딩 시간 측정
    this.trackPageLoadTime();
    
    // 에러 추적
    this.trackJavaScriptErrors();
    
    // 페이지 이탈 의도 추적
    this.trackExitIntent();
    
    // 앱 백그라운드 추적 (모바일)
    this.trackAppBackground();
  }

  // 페이지 로딩 시간 추적
  trackPageLoadTime() {
    window.addEventListener('load', () => {
      const loadTime = performance.now();
      if (loadTime > FEEDBACK_TRIGGERS.issues.slowLoading.threshold) {
        this.checkTrigger('issues', 'slowLoading', { loadTime });
      }
    });
  }

  // JavaScript 에러 추적
  trackJavaScriptErrors() {
    window.addEventListener('error', (event) => {
      console.error('JS Error detected:', event);
      this.checkTrigger('issues', 'jsError', { 
        error: event.message,
        filename: event.filename,
        lineno: event.lineno 
      });
    });
  }

  // 페이지 이탈 의도 추적
  trackExitIntent() {
    window.addEventListener('beforeunload', (event) => {
      // 세션이 충분히 길었다면 피드백 요청
      const sessionDuration = Date.now() - this.sessionData.startTime;
      if (sessionDuration > 60000) { // 1분 이상
        this.checkTrigger('events', 'pageExit', { sessionDuration });
      }
    });
  }

  // 앱 백그라운드 추적
  trackAppBackground() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        const sessionDuration = Date.now() - this.sessionData.startTime;
        if (sessionDuration > 120000) { // 2분 이상
          this.checkTrigger('events', 'appBackground', { sessionDuration });
        }
      }
    });
  }

  // 정답/오답 기록
  recordAnswer(isCorrect, questionType = null) {
    if (isCorrect) {
      this.sessionData.correctAnswers++;
      this.sessionData.correctStreak++;
      this.sessionData.wrongStreak = 0;
      
      // 연속 정답 트리거 확인
      if (this.sessionData.correctStreak >= FEEDBACK_TRIGGERS.success.correctStreak.threshold) {
        this.checkTrigger('success', 'correctStreak', { streak: this.sessionData.correctStreak });
      }
    } else {
      this.sessionData.wrongAnswers++;
      this.sessionData.wrongStreak++;
      this.sessionData.correctStreak = 0;
      
      // 문제 유형 추적
      if (questionType) {
        this.sessionData.recentMistakeTypes.push({
          type: questionType,
          timestamp: Date.now()
        });
        
        // 최근 10개만 유지
        if (this.sessionData.recentMistakeTypes.length > 10) {
          this.sessionData.recentMistakeTypes.shift();
        }
        
        // 같은 유형 반복 실수 확인
        const recentSameType = this.sessionData.recentMistakeTypes.filter(
          mistake => mistake.type === questionType && 
          Date.now() - mistake.timestamp < 600000 // 10분 내
        );
        
        if (recentSameType.length >= FEEDBACK_TRIGGERS.difficulty.repeatMistakes.threshold) {
          this.checkTrigger('difficulty', 'repeatMistakes', { 
            questionType, 
            mistakeCount: recentSameType.length 
          });
        }
      }
      
      // 연속 오답 트리거 확인
      if (this.sessionData.wrongStreak >= FEEDBACK_TRIGGERS.difficulty.wrongStreak.threshold) {
        this.checkTrigger('difficulty', 'wrongStreak', { streak: this.sessionData.wrongStreak });
      }
    }
  }

  // 시험 완료 기록
  recordExamCompletion(score) {
    this.sessionData.completedExams++;
    
    // 시험 완료 트리거
    this.checkTrigger('success', 'examCompleted', { 
      examNumber: this.sessionData.completedExams,
      score 
    });
    
    // 높은 점수 트리거
    if (score >= FEEDBACK_TRIGGERS.success.highScore.threshold) {
      this.checkTrigger('success', 'highScore', { score });
    }
  }

  // 기능 사용 기록
  recordFeatureUsage(featureName) {
    const isFirstTime = !this.sessionData.usedFeatures.has(featureName);
    this.sessionData.usedFeatures.add(featureName);
    
    if (isFirstTime) {
      this.checkTrigger('usage', 'featureDiscovery', { featureName });
    }
  }

  // 긴 세션 확인
  checkLongSession() {
    const sessionDuration = Date.now() - this.sessionData.startTime;
    if (sessionDuration >= FEEDBACK_TRIGGERS.usage.longSession.threshold) {
      this.checkTrigger('usage', 'longSession', { duration: sessionDuration });
    }
  }

  // 자주 방문하는 사용자 확인
  checkFrequentUser() {
    const recentVisits = this.getVisitDays();
    if (recentVisits >= FEEDBACK_TRIGGERS.usage.frequentUser.threshold) {
      this.checkTrigger('usage', 'frequentUser', { visitDays: recentVisits });
    }
  }

  // 트리거 조건 확인 및 실행
  checkTrigger(category, triggerName, context = {}) {
    const trigger = FEEDBACK_TRIGGERS[category]?.[triggerName];
    if (!trigger) return false;

    // 쿨다운 확인
    const cooldownKey = `${category}_${triggerName}`;
    const lastTrigger = this.sessionData.lastFeedbackTrigger[cooldownKey];
    
    if (lastTrigger && Date.now() - lastTrigger < trigger.cooldown) {
      return false; // 아직 쿨다운 중
    }

    // 사용자 동의 상태 확인
    const feedbackSettings = JSON.parse(localStorage.getItem('feedbackSettings') || '{"autoRequest": true}');
    if (!feedbackSettings.autoRequest) {
      return false;
    }

    // 트리거 실행
    this.executeTrigger(category, triggerName, trigger, context);
    
    // 쿨다운 업데이트
    this.sessionData.lastFeedbackTrigger[cooldownKey] = Date.now();
    
    return true;
  }

  // 트리거 실행
  executeTrigger(category, triggerName, trigger, context) {
    const triggerData = {
      category,
      triggerName,
      message: trigger.message,
      priority: trigger.priority,
      context,
      timestamp: Date.now(),
      isExitIntent: trigger.isExitIntent || false
    };

    // 지연 후 이벤트 발생
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('autoFeedbackRequest', {
        detail: triggerData
      }));
    }, trigger.delay);

    console.log('Auto feedback triggered:', triggerData);
  }

  // 설정 업데이트
  updateSettings(settings) {
    const currentSettings = JSON.parse(localStorage.getItem('feedbackSettings') || '{}');
    const newSettings = { ...currentSettings, ...settings };
    localStorage.setItem('feedbackSettings', JSON.stringify(newSettings));
  }

  // 정리
  cleanup() {
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.eventListeners = [];
  }
}

// 전역 인스턴스 (싱글톤)
let globalTriggerInstance = null;

export function getAutoFeedbackTrigger() {
  if (!globalTriggerInstance) {
    globalTriggerInstance = new AutoFeedbackTrigger();
  }
  return globalTriggerInstance;
}

// 편의 함수들
export const autoFeedbackActions = {
  recordCorrectAnswer: (questionType) => getAutoFeedbackTrigger().recordAnswer(true, questionType),
  recordWrongAnswer: (questionType) => getAutoFeedbackTrigger().recordAnswer(false, questionType),
  recordExamCompletion: (score) => getAutoFeedbackTrigger().recordExamCompletion(score),
  recordFeatureUsage: (featureName) => getAutoFeedbackTrigger().recordFeatureUsage(featureName),
  checkLongSession: () => getAutoFeedbackTrigger().checkLongSession(),
  updateSettings: (settings) => getAutoFeedbackTrigger().updateSettings(settings)
};